import { useRef, useState } from 'react'
import { Back } from 'shared/components/Nav/Back'

import { isConsole } from '../../../config/config'
import { Handler } from '../../../helpers/typeHelper'
import { User } from '../../../models/user'
import { ExecuteOnMount } from '../../ExecuteOnMount/ExecuteOnMount'
import { CDLInformationManual } from './CDLInformationParser/CDLInformationManual'
import { CDLForm } from './CDLInformationParser/CDLInformationParser.constants'
import { CDLInformationUpload } from './CDLInformationParser/CDLInformationUpload'
import { CheckrConsent } from './Checkr/CheckrConsent'
import { DacConsent } from './DacConsent'
import { DriverApplicationContainer, DriverApplicationContent } from './DriverApplication.styles'
import { DrivingHistory } from './DrivingHistory'
import { EmergencyContact } from './EmergencyContact'
import { HirerightApplication } from './HirerightApplication'
import { MedicalCard } from './MedicalCard'
import { PspConsent } from './PspConsent'
import { TenstreetUpload } from './Tenstreet/TenstreetUpload'

function getInitialStep(user: User, canEditDriverDetails: boolean): number {
  if (
    !user.driver?.hasSsn &&
    user.driver?.hadAtFaultIncident === null &&
    user.driver?.hadMajorViolation === null &&
    canEditDriverDetails
  ) {
    return 0
  }

  if (!user.driver?.isDriverApplicationFormComplete && canEditDriverDetails) {
    return 1
  }

  if (
    !(
      user.driver?.isLicenseInfoComplete &&
      user.driver.licenseIssued &&
      user.driver.licenseFront &&
      user.driver.licenseBack
    ) &&
    canEditDriverDetails
  ) {
    return 2
  }

  if (!user.driver?.isMedicalCardValid) {
    return 4
  }

  if (!user.driver?.isEmergencyContactComplete) {
    return 5
  }

  return 6
}

interface DriverApplicationProps {
  user: User
  handleRedirect: Handler
}

export function DriverApplication({ user, handleRedirect }: DriverApplicationProps) {
  const canEditDriverDetails =
    !user.driver.checkrCandidateId || !user.permissions?.checkrPackage1Onboarding?.enabled
  const initialStep = useRef(getInitialStep(user, canEditDriverDetails))
  const [errorInCheckr, setErrorInCheckr] = useState(false)
  const [activeStep, setActiveStep] = useState(initialStep.current)
  const [parsedCDL, setParsedCDL] = useState<CDLForm | null>(null)
  const onboardingCheckrFlag = user.permissions?.checkrPackage1Onboarding?.enabled

  const handleStepNavigation = (step: number) => {
    window.scrollTo({ top: 0 })
    setActiveStep(step)
  }

  const handleNext = () => {
    handleStepNavigation(activeStep + 1)
    setErrorInCheckr(false)
  }

  const handlePrevious = () => {
    if (!canEditDriverDetails && activeStep <= 4) {
      return handleRedirect()
    }
    handleStepNavigation(activeStep - 1)
    setErrorInCheckr(false)
  }

  const setCdlInfoStep = () => {
    handleStepNavigation(3)
    setErrorInCheckr(true)
  }

  const setCheckrStep = () => {
    handleStepNavigation(7)
    setErrorInCheckr(false)
  }

  function getStepContent(step: number) {
    switch (step) {
      case 0:
        return <TenstreetUpload nextStep={handleNext} user={user} handleRedirect={handleRedirect} />
      case 1:
        return (
          <DriverApplicationContent>
            <DrivingHistory nextStep={handleNext} user={user} handleRedirect={handleRedirect} />
          </DriverApplicationContent>
        )
      case 2:
        return (
          <DriverApplicationContent>
            <CDLInformationUpload nextStep={handleNext} user={user} setParsedCDL={setParsedCDL} />
          </DriverApplicationContent>
        )
      case 3:
        return (
          <DriverApplicationContent>
            <CDLInformationManual
              nextStep={errorInCheckr ? setCheckrStep : handleNext}
              user={user}
              parsedCDL={parsedCDL}
              setParsedCDL={setParsedCDL}
            />
          </DriverApplicationContent>
        )
      case 4:
        return <MedicalCard nextStep={handleNext} user={user} />

      case 5:
        return (
          <DriverApplicationContent>
            <EmergencyContact nextStep={handleNext} user={user} />
          </DriverApplicationContent>
        )
      case 6:
        return isConsole() ? (
          <ExecuteOnMount onMount={handleRedirect} />
        ) : (
          <PspConsent user={user} onSubmit={handleNext} onBack={handlePrevious} />
        )
      case 7:
        return isConsole() ? (
          <ExecuteOnMount onMount={handleRedirect} />
        ) : (
          <DacConsent user={user} onSubmit={handleNext} onBack={() => handleStepNavigation(5)} />
        )
      case 8:
        return isConsole() ? (
          <ExecuteOnMount onMount={handleRedirect} />
        ) : onboardingCheckrFlag ? (
          <CheckrConsent
            user={user}
            nextStep={handleNext}
            previousStep={() => handleStepNavigation(5)} // On back it should skip the `CheckrDocusignConsent`
            setCdlInfoStep={setCdlInfoStep}
          />
        ) : (
          <HirerightApplication nextStep={handleNext} user={user} />
        )
      case 9:
        return <ExecuteOnMount onMount={handleRedirect} />

      default:
    }
  }

  if (
    activeStep === 0 ||
    activeStep === 6 ||
    activeStep === 7 ||
    (activeStep === 8 && onboardingCheckrFlag)
  ) {
    // No container and back button here
    return <>{getStepContent(activeStep)}</>
  }

  return (
    <DriverApplicationContainer>
      <Back onClick={handlePrevious} />
      {getStepContent(activeStep)}
    </DriverApplicationContainer>
  )
}
